import { useState, useRef, useEffect } from "react";
import DOMPurify from 'dompurify';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmailAutocomplete } from "./EmailAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  X, 
  Paperclip, 
  Send,
  Trash2,
  Minus,
  Maximize2,
  Upload,
  BookOpen,
  FileText,
  Image,
  File,
  Check,
  Sparkles
} from "lucide-react";
import { AIComposeDialog } from "./AIComposeDialog";
import { cn } from "@/lib/utils";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { processSignature } from "@/lib/signatureUtils";
import type { KnowledgeBaseAttachment } from "@/types/team";

interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  color?: string;
}

interface CustomerFile {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
}

interface EmailComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromAddress?: string;
  onEmailSent?: () => void;
  senderName?: string;
  organization?: Organization;
  accountId?: string;
  organizationId?: string;
  customerId?: string; // Added for customer file attachments
  replyTo?: {
    to: string;
    subject: string;
    body?: string;
    originalHtml?: string;
    mode: 'reply' | 'replyAll' | 'forward';
    cc?: string;
    attachments?: any[];
    emailId?: string; // ID of the original email for attachment loading
  } | null;
  userProfile?: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    address?: string;
    contact_email?: string;
    avatar_url?: string;
  } | null;
}

interface Attachment {
  file: File;
  name: string;
  size: number;
  source?: 'upload' | 'knowledge-base' | 'customer-file';
  sourceLabel?: string; // e.g. KB entry title or "Kundenakte"
}

interface OrganizationSignature {
  id: string;
  signature_html: string;
  signature_name: string;
  is_default: boolean;
}

export const EmailComposeDialog = ({ 
  open, 
  onOpenChange,
  fromAddress = "",
  onEmailSent,
  senderName,
  organization,
  accountId,
  organizationId,
  customerId,
  replyTo,
  userProfile
}: EmailComposeDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState<string>("");
  const [showKnowledgeBaseDialog, setShowKnowledgeBaseDialog] = useState(false);
  const [showCustomerFilesDialog, setShowCustomerFilesDialog] = useState(false);
  const [showAIComposeDialog, setShowAIComposeDialog] = useState(false);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [knowledgeBaseEntries, setKnowledgeBaseEntries] = useState<{
    id: string;
    title: string;
    attachments: KnowledgeBaseAttachment[];
  }[]>([]);
  const [customerFiles, setCustomerFiles] = useState<CustomerFile[]>([]);
  const [loadingKB, setLoadingKB] = useState(false);
  const [loadingCustomerFiles, setLoadingCustomerFiles] = useState(false);
  
  // Store raw signature HTML separately from processed version
  const [rawSignatureHtml, setRawSignatureHtml] = useState<string>("");
  
  // Load organization signature when dialog opens
  // Use organizationId prop or fall back to organization.id
  const effectiveOrgId = organizationId || organization?.id;
  
  useEffect(() => {
    const loadSignature = async () => {
      if (!effectiveOrgId || !open) return;
      
      try {
        const { data, error } = await supabase
          .from('organization_signatures')
          .select('*')
          .eq('organization_id', effectiveOrgId)
          .eq('is_default', true)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error loading signature:', error);
          return;
        }
        
        if (data) {
          setRawSignatureHtml(data.signature_html);
        }
      } catch (error) {
        console.error('Error loading signature:', error);
      }
    };
    
    loadSignature();
  }, [effectiveOrgId, open]);
  
  // Process signature whenever raw signature or profile/organization data changes
  useEffect(() => {
    if (rawSignatureHtml) {
      const processedSignature = processSignature(
        rawSignatureHtml,
        userProfile,
        organization
      );
      setSignatureHtml(processedSignature);
    }
  }, [rawSignatureHtml, userProfile, organization]);

  // Track if the dialog has been initialized to prevent overwriting user content
  const initializedRef = useRef(false);
  
  // Populate fields when the dialog opens programmatically (no DialogTrigger)
  // but only ONCE per open-session to avoid wiping the user's draft.
  useEffect(() => {
    if (!open) return;
    if (!replyTo) return;
    if (initializedRef.current) return;

    initializedRef.current = true;
    setTo(replyTo.mode === "forward" ? "" : replyTo.to);
    setSubject(replyTo.subject);
    setBody(replyTo.body || "");

    const shouldShowCc = !!(replyTo.cc && replyTo.mode === "replyAll");
    setShowCc(shouldShowCc);
    setCc(shouldShowCc ? replyTo.cc || "" : "");
  }, [replyTo, open]);
  
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const closeResetTimeoutRef = useRef<number | null>(null);
  const pickingFileRef = useRef(false);
  const filePickerFocusTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const onWindowFocus = () => {
      // When the user returns from the native file picker.
      // IMPORTANT: Keep the "picking" flag a bit longer, because some browsers
      // fire Radix/Dialog close intents *after* focus returns, which would
      // otherwise reset the draft.
      if (!pickingFileRef.current) return;

      if (filePickerFocusTimeoutRef.current) {
        window.clearTimeout(filePickerFocusTimeoutRef.current);
      }

      filePickerFocusTimeoutRef.current = window.setTimeout(() => {
        pickingFileRef.current = false;
        filePickerFocusTimeoutRef.current = null;
      }, 800);
    };
    window.addEventListener("focus", onWindowFocus);
    return () => {
      window.removeEventListener("focus", onWindowFocus);
      if (filePickerFocusTimeoutRef.current) {
        window.clearTimeout(filePickerFocusTimeoutRef.current);
        filePickerFocusTimeoutRef.current = null;
      }
    };
  }, []);

  const resetDraft = () => {
    setTo("");
    setCc("");
    setBcc("");
    setSubject("");
    setBody("");
    setShowCc(false);
    setShowBcc(false);
    setAttachments([]);
  };

  // Reset or populate when dialog opens with replyTo.
  // NOTE: We delay auto-reset on close to avoid losing content if Radix
  // briefly closes/reopens the dialog (e.g. when opening the native file picker).
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      if (closeResetTimeoutRef.current) {
        window.clearTimeout(closeResetTimeoutRef.current);
        closeResetTimeoutRef.current = null;
      }

      // Only populate from replyTo on first open, not on re-opens (which would overwrite user content)
      if (replyTo && !initializedRef.current) {
        initializedRef.current = true;
        setTo(replyTo.mode === "forward" ? "" : replyTo.to);
        setSubject(replyTo.subject);
        setBody(replyTo.body || "");
        if (replyTo.cc && replyTo.mode === "replyAll") {
          setCc(replyTo.cc);
          setShowCc(true);
        }
      } else if (!initializedRef.current) {
        // Mark as initialized even without replyTo
        initializedRef.current = true;
      }

      onOpenChange(true);
      return;
    }

    // Don't reset if a nested dialog is open (file picker, knowledge base, customer files, AI compose, attachment menu)
    if (pickingFileRef.current || showKnowledgeBaseDialog || showCustomerFilesDialog || showAIComposeDialog || isAttachmentMenuOpen) {
      // Keep the dialog open but don't reset anything
      return;
    }

    if (closeResetTimeoutRef.current) {
      window.clearTimeout(closeResetTimeoutRef.current);
    }
    closeResetTimeoutRef.current = window.setTimeout(() => {
      if (!openRef.current && !showKnowledgeBaseDialog && !showCustomerFilesDialog && !showAIComposeDialog && !isAttachmentMenuOpen && !pickingFileRef.current) {
        resetDraft();
        // Reset initialization flag when dialog fully closes
        initializedRef.current = false;
      }
    }, 250);

    onOpenChange(false);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const openLocalFilePicker = () => {
    pickingFileRef.current = true;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (filePickerFocusTimeoutRef.current) {
      window.clearTimeout(filePickerFocusTimeoutRef.current);
      filePickerFocusTimeoutRef.current = null;
    }
    pickingFileRef.current = false;
    const files = e.target.files;
    if (files) {
      const newAttachments = Array.from(files).map((file) => ({
        file,
        name: file.name,
        size: file.size,
        source: 'upload' as const,
        sourceLabel: 'Hochgeladen',
      }));
      setAttachments((prev) => [...prev, ...newAttachments]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Load knowledge base entries with attachments
  const loadKnowledgeBaseAttachments = async () => {
    if (!user) return;
    
    setLoadingKB(true);
    try {
      // Get user's team_id from profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profileData?.team_id) {
        toast({ title: "Kein Team gefunden", variant: "destructive" });
        return;
      }

      const { data, error } = await supabase
        .from('team_knowledge_base')
        .select('id, title, attachments')
        .eq('team_id', profileData.team_id)
        .not('attachments', 'is', null)
        .order('title');

      if (error) throw error;

      // Filter entries that have attachments
      const entriesWithAttachments = (data || [])
        .map(entry => ({
          id: entry.id,
          title: entry.title,
          attachments: (entry.attachments as unknown as KnowledgeBaseAttachment[]) || []
        }))
        .filter(entry => entry.attachments.length > 0);

      setKnowledgeBaseEntries(entriesWithAttachments);
      setShowKnowledgeBaseDialog(true);
    } catch (error) {
      console.error('Error loading knowledge base:', error);
      toast({ title: "Fehler beim Laden der Wissensbank", variant: "destructive" });
    } finally {
      setLoadingKB(false);
    }
  };

  // Load customer files
  const loadCustomerFiles = async () => {
    if (!customerId) {
      toast({ title: "Kein Kunde ausgewählt", variant: "destructive" });
      return;
    }
    
    setLoadingCustomerFiles(true);
    try {
      const { data, error } = await supabase
        .from('customer_files')
        .select('id, file_name, file_path, file_size, file_type')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCustomerFiles(data || []);
      setShowCustomerFilesDialog(true);
    } catch (error) {
      console.error('Error loading customer files:', error);
      toast({ title: "Fehler beim Laden der Kundendateien", variant: "destructive" });
    } finally {
      setLoadingCustomerFiles(false);
    }
  };

  // Add customer file attachment to email
  const addCustomerFileAttachment = async (file: CustomerFile) => {
    try {
      // Get the file from Supabase storage
      const { data: fileData, error } = await supabase.storage
        .from('customer-files')
        .download(file.file_path);

      if (error) throw error;
      
      const fileObj = new globalThis.File([fileData], file.file_name, { type: file.file_type });
      
      // Check if already attached
      if (attachments.some(a => a.name === file.file_name)) {
        toast({ title: "Datei bereits angehängt" });
        return;
      }
      
      setAttachments(prev => [...prev, {
        file: fileObj,
        name: file.file_name,
        size: file.file_size,
        source: 'customer-file',
        sourceLabel: 'Kundenakte',
      }]);
    } catch (error) {
      console.error('Error adding customer file attachment:', error);
      toast({ title: "Fehler beim Anhängen der Datei", variant: "destructive" });
    }
  };

  // Add knowledge base attachment to email
  const addKnowledgeBaseAttachment = async (attachment: KnowledgeBaseAttachment, entryTitle?: string) => {
    try {
      // Fetch the file from storage
      const response = await fetch(attachment.url);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const fileObj = new globalThis.File([blob], attachment.name, { type: attachment.type });
      
      // Check if already attached
      if (attachments.some(a => a.name === attachment.name)) {
        toast({ title: "Datei bereits angehängt" });
        return;
      }
      
      setAttachments(prev => [...prev, {
        file: fileObj,
        name: attachment.name,
        size: attachment.size,
        source: 'knowledge-base',
        sourceLabel: entryTitle || 'Wissensbank',
      }]);
    } catch (error) {
      console.error('Error adding attachment:', error);
      toast({ title: "Fehler beim Anhängen der Datei", variant: "destructive" });
    }
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return Image;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result as string;
          // Remove data URL prefix (e.g., "data:application/pdf;base64,")
          const base64 = result.split(',')[1];
          if (!base64) {
            reject(new Error(`Datei ${file.name} konnte nicht in Base64 umgewandelt werden`));
            return;
          }
          resolve(base64);
        } catch (err) {
          reject(new Error(`Fehler beim Verarbeiten der Datei ${file.name}`));
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(new Error(`Fehler beim Lesen der Datei ${file.name}`));
      };
      reader.readAsDataURL(file);
    });
  };

  const convertToHtml = (text: string, includeQuote?: boolean): string => {
    // Escape HTML entities
    const escapeHtml = (str: string) => str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    
    // Extract the new message content (before the quoted section separator)
    let newContent = text;
    let quotedSection = '';
    if (text.includes('--------')) {
      const parts = text.split(/\n\n\n--------/);
      newContent = parts[0] || '';
      if (includeQuote && parts.length > 1) {
        quotedSection = '--------' + parts.slice(1).join('\n\n\n--------');
      }
    }
    
    // Convert new content to HTML - preserve line breaks
    const html = escapeHtml(newContent.trim())
      .split('\n')
      .map(line => line || '<br>')
      .join('<br>\n');
    
    let result = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; line-height: 1.5;">${html}</div>`;
    
    // Append quoted original message if available
    if (includeQuote && quotedSection) {
      // If we have the original HTML, use that for better formatting
      if (replyTo?.originalHtml) {
        const headerMatch = quotedSection.match(/(-------- (?:Ursprüngliche|Weitergeleitete) Nachricht --------\n(?:Von:.*\n)?(?:Datum:.*\n)?(?:Betreff:.*\n)?)/);
        const headerText = headerMatch ? escapeHtml(headerMatch[1]).replace(/\n/g, '<br>') : '';
        result += `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; color: #666;">
          <div style="margin-bottom: 8px; font-size: 12px;">${headerText}</div>
          <blockquote style="margin: 0; padding-left: 12px; border-left: 2px solid #ccc;">
            ${replyTo.originalHtml}
          </blockquote>
        </div>`;
      } else {
        // Fallback: convert quoted plain text to HTML
        const quotedHtml = escapeHtml(quotedSection.trim()).split('\n').map(line => line || '<br>').join('<br>\n');
        result += `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; color: #666;">
          <blockquote style="margin: 0; padding-left: 12px; border-left: 2px solid #ccc;">${quotedHtml}</blockquote>
        </div>`;
      }
    }
    
    return result;
  };

  const handleSend = async () => {
    if (!to.trim()) return;
    
    setSending(true);
    
    try {
      // Fetch user's SMTP credentials from their email account
      let smtpCredentials: { host: string; port: number; username: string; password: string } | undefined;
      if (user && accountId) {
        const { data: emailAccount } = await supabase
          .from('email_accounts')
          .select('smtp_host, smtp_port, smtp_username, smtp_password, email_address')
          .eq('id', accountId)
          .single();
        
        if (emailAccount?.smtp_host && emailAccount?.smtp_password) {
          smtpCredentials = {
            host: emailAccount.smtp_host,
            port: emailAccount.smtp_port || 587,
            username: emailAccount.smtp_username || emailAccount.email_address,
            password: emailAccount.smtp_password,
          };
        }
      } else if (user) {
        // Fallback: get default account
        const { data: emailAccount } = await supabase
          .from('email_accounts')
          .select('smtp_host, smtp_port, smtp_username, smtp_password, email_address')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('is_default', { ascending: false })
          .limit(1)
          .single();
        
        if (emailAccount?.smtp_host && emailAccount?.smtp_password) {
          smtpCredentials = {
            host: emailAccount.smtp_host,
            port: emailAccount.smtp_port || 587,
            username: emailAccount.smtp_username || emailAccount.email_address,
            password: emailAccount.smtp_password,
          };
        }
      }

      // Convert attachments to base64 with better error handling
      let attachmentData: Array<{ filename: string; content: string; contentType: string }> = [];
      
      if (attachments.length > 0) {
        try {
          attachmentData = await Promise.all(
            attachments.map(async (att) => {
              console.log(`Processing attachment: ${att.name}, size: ${att.size}, type: ${att.file.type}`);
              const content = await fileToBase64(att.file);
              console.log(`Successfully converted ${att.name} to base64`);
              return {
                filename: att.name,
                content,
                contentType: att.file.type || 'application/octet-stream'
              };
            })
          );
        } catch (attachmentError: any) {
          console.error('Error processing attachments:', attachmentError);
          toast({
            title: "Fehler bei Anhängen",
            description: attachmentError.message || "Ein Anhang konnte nicht verarbeitet werden.",
            variant: "destructive",
          });
          setSending(false);
          return;
        }
      }

      // Convert plain text to proper HTML with formatting preserved
      // For replies/forwards, include the quoted original message
      let htmlBody = convertToHtml(body, !!replyTo);
      
      // Append signature if available
      if (signatureHtml) {
        htmlBody += `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #e5e5e5;">${signatureHtml}</div>`;
      }

      // Retry logic for mobile reliability
      let sendSuccess = false;
      let lastError = null;
      const maxAttempts = 2;
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log(`Email send attempt ${attempt}/${maxAttempts}`);
          const { data, error } = await supabase.functions.invoke('send-email', {
            body: {
              to: to.split(',').map(e => e.trim()).filter(Boolean),
              cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : undefined,
              bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : undefined,
              subject: subject || "(Kein Betreff)",
              html: htmlBody,
              text: body + (signatureHtml ? '\n\n---\n' + signatureHtml.replace(/<[^>]+>/g, '') : ''),
              from: fromAddress,
              fromName: senderName,
              organization: organization ? {
                name: organization.name,
                logoUrl: organization.logo_url,
                color: organization.color
              } : undefined,
              attachments: attachmentData.length > 0 ? attachmentData : undefined,
              smtp: smtpCredentials,
            }
          });

          if (error) throw error;
          sendSuccess = true;
          console.log('Email sent successfully on attempt', attempt);
          break;
        } catch (e: any) {
          console.warn(`Email send attempt ${attempt} failed:`, e);
          lastError = e;
          if (attempt < maxAttempts) {
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (!sendSuccess) {
        throw lastError || new Error('E-Mail konnte nicht gesendet werden');
      }

      // Speichere gesendete E-Mail in der Datenbank (non-blocking)
      if (accountId && organizationId) {
        supabase.auth.getUser().then(({ data: { user } }) => {
          if (user) {
            const toAddresses = to.split(',').map(e => e.trim()).filter(Boolean);
            const ccAddresses = cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : null;
            const bccAddresses = bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : null;
            
            supabase.from('emails').insert({
              user_id: user.id,
              account_id: accountId,
              organization_id: organizationId,
              from_address: fromAddress,
              to_addresses: toAddresses,
              cc_addresses: ccAddresses,
              bcc_addresses: bccAddresses,
              subject: subject || "(Kein Betreff)",
              body_html: htmlBody,
              body_text: body,
              status: 'sent',
              sent_at: new Date().toISOString(),
              is_read: true,
              attachments: attachments.length > 0 ? attachments.map(att => ({
                filename: att.name,
                size: att.size,
                contentType: att.file.type || 'application/octet-stream'
              })) : null
            }).then(({ error }) => {
              if (error) console.error('Error saving sent email to DB:', error);
            });
          }
        });
      }

      toast({
        title: "E-Mail gesendet",
        description: `Nachricht an ${to} wurde erfolgreich gesendet.`,
      });

      onEmailSent?.();
      handleClose();
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Fehler beim Senden",
        description: error.message || "Die E-Mail konnte nicht gesendet werden. Bitte erneut versuchen.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (closeResetTimeoutRef.current) {
      window.clearTimeout(closeResetTimeoutRef.current);
      closeResetTimeoutRef.current = null;
    }
    if (filePickerFocusTimeoutRef.current) {
      window.clearTimeout(filePickerFocusTimeoutRef.current);
      filePickerFocusTimeoutRef.current = null;
    }
    pickingFileRef.current = false;
    resetDraft();
    initializedRef.current = false;
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-xl w-[calc(100%-2rem)] h-[90vh] sm:h-[80vh] flex flex-col p-0 gap-0 rounded-t-xl sm:rounded-xl overflow-hidden fixed bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 left-1/2 -translate-x-1/2 [&>button]:hidden"
        onPointerDownOutside={(e) => {
            // Compose should never dismiss on outside interactions (prevents losing draft
            // when using portaled menus or the native file picker).
            e.preventDefault();
        }}
        onFocusOutside={(e) => {
            // Same as above: keep draft stable even if focus temporarily leaves the document.
            e.preventDefault();
        }}
        onInteractOutside={(e) => {
            // Prevent dismiss on any outside interaction.
            e.preventDefault();
        }}
      >
        <VisuallyHidden>
          <DialogTitle>
            {replyTo?.mode === 'reply' ? 'Antworten' : replyTo?.mode === 'replyAll' ? 'Allen antworten' : replyTo?.mode === 'forward' ? 'Weiterleiten' : 'Neue Nachricht'}
          </DialogTitle>
          <DialogDescription>Verfassen Sie eine E-Mail</DialogDescription>
        </VisuallyHidden>
        
        {/* Header - Apple Mail style */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/80 border-b shrink-0">
          <span className="font-medium text-sm">
            {replyTo?.mode === 'reply' ? 'Antworten' : replyTo?.mode === 'replyAll' ? 'Allen antworten' : replyTo?.mode === 'forward' ? 'Weiterleiten' : 'Neue Nachricht'}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 hidden sm:flex">
              <Minus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 hidden sm:flex">
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Compact form fields */}
        <div className="flex-1 overflow-y-auto">
          {/* From */}
          <div className="flex items-center border-b px-4 py-2 text-sm">
            <span className="text-muted-foreground w-12 shrink-0">Von</span>
            <span className="text-foreground truncate">{fromAddress || "—"}</span>
          </div>

          {/* To */}
          <div className="flex items-center border-b px-4 py-1.5">
            <span className="text-muted-foreground text-sm w-12 shrink-0">An</span>
            <EmailAutocomplete
              value={to}
              onChange={setTo}
              placeholder="Empfänger"
              className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
              organizationId={organizationId}
            />
            <div className="flex items-center gap-1 shrink-0 text-xs">
              {!showCc && (
                <button 
                  onClick={() => setShowCc(true)}
                  className="text-muted-foreground hover:text-foreground px-1"
                >
                  Cc
                </button>
              )}
              {!showBcc && (
                <button 
                  onClick={() => setShowBcc(true)}
                  className="text-muted-foreground hover:text-foreground px-1"
                >
                  Bcc
                </button>
              )}
            </div>
          </div>

          {/* CC */}
          {showCc && (
            <div className="flex items-center border-b px-4 py-1.5">
              <span className="text-muted-foreground text-sm w-12 shrink-0">Cc</span>
              <EmailAutocomplete
                value={cc}
                onChange={setCc}
                placeholder="CC-Empfänger"
                className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
                organizationId={organizationId}
              />
              <button 
                onClick={() => { setShowCc(false); setCc(""); }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* BCC */}
          {showBcc && (
            <div className="flex items-center border-b px-4 py-1.5">
              <span className="text-muted-foreground text-sm w-12 shrink-0">Bcc</span>
              <EmailAutocomplete
                value={bcc}
                onChange={setBcc}
                placeholder="BCC-Empfänger"
                className="border-0 shadow-none px-0 h-8 text-sm focus-visible:ring-0 bg-transparent"
                organizationId={organizationId}
              />
              <button 
                onClick={() => { setShowBcc(false); setBcc(""); }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Subject */}
          <div className="flex items-start border-b px-4 py-1.5">
            <span className="text-muted-foreground text-sm w-16 shrink-0 pt-1.5">Betreff</span>
            <Textarea 
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = target.scrollHeight + 'px';
              }}
              placeholder="Betreff eingeben"
              rows={1}
              className="border-0 shadow-none px-0 min-h-[32px] text-sm focus-visible:ring-0 bg-transparent ml-2 resize-none py-1.5"
            />
          </div>

          {/* Body - New message area with AI compose hint */}
          <div className="relative">
            <Textarea 
              value={replyTo ? body.split('\n\n\n--------')[0] : body}
              onChange={(e) => {
                if (replyTo) {
                  const quotedPart = body.includes('\n\n\n--------') ? '\n\n\n--------' + body.split('\n\n\n--------')[1] : '';
                  setBody(e.target.value + quotedPart);
                } else {
                  setBody(e.target.value);
                }
              }}
              placeholder=""
              className="border-0 shadow-none rounded-none min-h-[120px] sm:min-h-[150px] text-sm resize-none focus-visible:ring-0 px-4 py-3"
            />
            {/* Placeholder with AI compose hint */}
            {!(replyTo ? body.split('\n\n\n--------')[0] : body).trim() && (
              <div className="absolute left-4 top-3 flex items-center gap-1 text-sm pointer-events-none">
                <span className="text-muted-foreground/50">Nachricht verfassen...</span>
                <button
                  type="button"
                  onClick={() => setShowAIComposeDialog(true)}
                  className="pointer-events-auto flex items-center gap-1 text-primary/70 hover:text-primary transition-colors cursor-pointer hover:underline"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>mit KI verfassen</span>
                </button>
              </div>
            )}
          </div>

          {/* Signature Preview */}
          {signatureHtml && (
            <div className="border-t border-border/50 px-4 py-3 bg-muted/20">
              <div 
                className="text-sm [&_p]:mb-3 [&_p]:mt-0 [&_p:empty]:h-4 [&_br]:block [&_div]:mb-2 [&_img]:inline-block"
                dangerouslySetInnerHTML={{ __html: signatureHtml }}
              />
            </div>
          )}

          {/* Quoted original message - Apple Mail style */}
          {replyTo && replyTo.body && (replyTo.body.includes('-------- Ursprüngliche Nachricht') || replyTo.body.includes('-------- Weitergeleitete Nachricht')) && (
            <div className="border-t border-border/50 bg-muted/30 max-h-[400px] overflow-y-auto">
              <div className="px-4 py-3 border-l-2 border-primary/40 ml-4 mr-4 my-3">
                <div className="text-xs text-muted-foreground mb-2 font-medium">
                  {replyTo.mode === 'forward' ? 'Weitergeleitete Nachricht' : 'Ursprüngliche Nachricht'}
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {(() => {
                    // Split at the separator line
                    const separatorMatch = replyTo.body.match(/-------- (Ursprüngliche|Weitergeleitete) Nachricht --------/);
                    if (!separatorMatch) return null;
                    
                    const separatorIndex = replyTo.body.indexOf(separatorMatch[0]);
                    const afterSeparator = replyTo.body.slice(separatorIndex + separatorMatch[0].length).trim();
                    const lines = afterSeparator.split('\n');
                    const metaLines: string[] = [];
                    let bodyStartIndex = 0;
                    
                    // Extract metadata lines (Von, Datum, Betreff, An)
                    for (let i = 0; i < lines.length; i++) {
                      const line = lines[i].trim();
                      if (line.startsWith('Von:') || line.startsWith('Datum:') || 
                          line.startsWith('Betreff:') || line.startsWith('An:')) {
                        metaLines.push(line);
                        bodyStartIndex = i + 1;
                      } else if (line === '' && metaLines.length > 0) {
                        bodyStartIndex = i + 1;
                        break;
                      } else if (metaLines.length > 0) {
                        bodyStartIndex = i;
                        break;
                      }
                    }
                    
                    const textBody = lines.slice(bodyStartIndex).join('\n').trim();
                    
                    return (
                      <>
                        {metaLines.map((line, idx) => {
                          const colonIndex = line.indexOf(':');
                          const label = line.slice(0, colonIndex);
                          const value = line.slice(colonIndex + 1).trim();
                          return (
                            <div key={idx} className="flex gap-2 text-xs">
                              <span className="font-medium shrink-0 text-muted-foreground">{label}:</span>
                              <span className="break-all text-foreground/70">{value}</span>
                            </div>
                          );
                        })}
                        <div className="mt-3 pt-3 border-t border-border/30">
                          {replyTo.originalHtml ? (
                            <div 
                              className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 [&_img]:max-w-full [&_img]:h-auto"
                              dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(replyTo.originalHtml, {
                                  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'img', 'hr'],
                                  ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target', 'width', 'height'],
                                  ALLOW_DATA_ATTR: false
                                })
                              }}
                            />
                          ) : textBody ? (
                            <div className="whitespace-pre-wrap font-sans leading-relaxed text-foreground/80 text-sm">
                              {textBody}
                            </div>
                          ) : null}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Attachments - grouped by source */}
          {attachments.length > 0 && (
            <div className="px-3 sm:px-4 py-2 border-t space-y-2">
              {/* Group attachments by sourceLabel */}
              {Object.entries(
                attachments.reduce((acc, att, idx) => {
                  const label = att.sourceLabel || 'Hochgeladen';
                  if (!acc[label]) acc[label] = [];
                  acc[label].push({ ...att, originalIndex: idx });
                  return acc;
                }, {} as Record<string, (Attachment & { originalIndex: number })[]>)
              ).map(([sourceLabel, atts]) => (
                <div key={sourceLabel} className="space-y-1">
                  <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">{sourceLabel}</p>
                  <div className="flex flex-col gap-1">
                    {atts.map((attachment) => (
                      <div 
                        key={attachment.originalIndex}
                        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1.5 rounded-lg bg-muted text-xs group max-w-full"
                      >
                        <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="flex-1 min-w-0 break-all text-[11px] sm:text-xs leading-tight">{attachment.name}</span>
                        <span className="text-muted-foreground text-[10px] sm:text-xs shrink-0">
                          {formatFileSize(attachment.size)}
                        </span>
                        <button
                          onClick={() => removeAttachment(attachment.originalIndex)}
                          className="p-0.5 opacity-60 hover:opacity-100 shrink-0"
                        >
                          <X className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer toolbar */}
        <div className="px-3 py-2 border-t shrink-0 flex items-center justify-between gap-2 bg-background">
          <div className="flex items-center gap-1">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
            <DropdownMenu 
              modal={false} 
              open={isAttachmentMenuOpen} 
              onOpenChange={setIsAttachmentMenuOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  disabled={sending || loadingKB || loadingCustomerFiles}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal container={document.body}>
                <DropdownMenuContent 
                  align="start" 
                  className="z-[9999] bg-popover border shadow-lg"
                  style={{ pointerEvents: 'auto' }}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <DropdownMenuItem onSelect={(e) => {
                    e.preventDefault();
                    pickingFileRef.current = true;
                    setIsAttachmentMenuOpen(false);
                    setTimeout(() => {
                      fileInputRef.current?.click();
                    }, 100);
                  }}>
                    <Upload className="h-4 w-4 mr-2" />
                    Datei hochladen
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={(e) => {
                    e.preventDefault();
                    pickingFileRef.current = true;
                    setIsAttachmentMenuOpen(false);
                    setTimeout(() => {
                      pickingFileRef.current = false;
                      loadKnowledgeBaseAttachments();
                    }, 100);
                  }}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Aus Wissensbank
                  </DropdownMenuItem>
                  {customerId && (
                    <DropdownMenuItem onSelect={(e) => {
                      e.preventDefault();
                      pickingFileRef.current = true;
                      setIsAttachmentMenuOpen(false);
                      setTimeout(() => {
                        pickingFileRef.current = false;
                        loadCustomerFiles();
                      }, 100);
                    }}>
                      <File className="h-4 w-4 mr-2" />
                      Aus Kundenakte
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:text-destructive"
              onClick={handleClose}
              disabled={sending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!to.trim() || sending}
            className="px-4"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Senden..." : "Senden"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      {/* Knowledge Base Attachment Dialog - Outside main dialog to prevent state reset */}
      <Dialog open={showKnowledgeBaseDialog} onOpenChange={setShowKnowledgeBaseDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Anhänge aus Wissensbank
            </DialogTitle>
            <DialogDescription>
              Wähle Dateien aus der Wissensbank zum Anhängen an diese E-Mail
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            {knowledgeBaseEntries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Keine Anhänge in der Wissensbank gefunden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {knowledgeBaseEntries.map(entry => (
                  <div key={entry.id} className="border rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2">{entry.title}</h4>
                    <div className="space-y-1.5">
                      {entry.attachments.map((attachment, idx) => {
                        const FileIcon = getFileIcon(attachment.type);
                        const isAttached = attachments.some(a => a.name === attachment.name);
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              if (isAttached) {
                                // Remove attachment by name
                                setAttachments(prev => prev.filter(a => a.name !== attachment.name));
                              } else {
                                addKnowledgeBaseAttachment(attachment, entry.title);
                              }
                            }}
                            className={cn(
                              "w-full flex items-center gap-2 p-2 rounded-md text-left text-sm transition-colors",
                              isAttached 
                                ? "bg-primary/10 text-primary hover:bg-destructive/10 hover:text-destructive" 
                                : "hover:bg-muted"
                            )}
                          >
                            <FileIcon className="h-4 w-4 shrink-0" />
                            <span className="flex-1">{attachment.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {formatFileSize(attachment.size)}
                            </span>
                            {isAttached && (
                              <Check className="h-4 w-4 text-primary shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowKnowledgeBaseDialog(false)}>
              Fertig
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Files Attachment Dialog - Outside main dialog to prevent state reset */}
      <Dialog open={showCustomerFilesDialog} onOpenChange={setShowCustomerFilesDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Kundendateien
            </DialogTitle>
            <DialogDescription>
              Wähle Dateien aus der Kundenakte zum Anhängen an diese E-Mail
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            {customerFiles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Keine Dateien für diesen Kunden gefunden</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {customerFiles.map((file) => {
                  const FileIcon = getFileIcon(file.file_type);
                  const isAttached = attachments.some(a => a.name === file.file_name);
                  
                  return (
                    <button
                      key={file.id}
                      onClick={() => addCustomerFileAttachment(file)}
                      disabled={isAttached}
                      className={cn(
                        "w-full flex items-center gap-2 p-2 rounded-md text-left text-sm transition-colors",
                        isAttached 
                          ? "bg-primary/10 text-primary cursor-default" 
                          : "hover:bg-muted"
                      )}
                    >
                      <FileIcon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate">{file.file_name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatFileSize(file.file_size)}
                      </span>
                      {isAttached && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCustomerFilesDialog(false)}>
              Fertig
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Compose Dialog - Outside main dialog to prevent state reset */}
      <AIComposeDialog
        open={showAIComposeDialog}
        onOpenChange={setShowAIComposeDialog}
        onAccept={(emailText, suggestedSubject) => {
          // Set the generated email as the body
          if (replyTo) {
            const quotedPart = body.includes('\n\n\n--------') ? '\n\n\n--------' + body.split('\n\n\n--------')[1] : '';
            setBody(emailText + quotedPart);
          } else {
            setBody(emailText);
          }
          // Set suggested subject if provided and current subject is empty
          if (suggestedSubject && !subject.trim()) {
            setSubject(suggestedSubject);
          }
        }}
        originalEmail={replyTo ? {
          from: replyTo.to,
          subject: replyTo.subject,
          body: replyTo.body?.split('\n\n\n--------')[1] || replyTo.body,
          attachments: replyTo.attachments,
          emailId: replyTo.emailId,
        } : null}
        recipientEmail={to.trim() || undefined}
        userName={userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : undefined}
        signatureText={signatureHtml ? signatureHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : undefined}
        currentSubject={subject}
        organizationId={organizationId}
      />
    </>
  );
};
